import type { MenuItem } from '@/lib/types/pos'

function normalizeMenuSearchText(value: string | null | undefined): string[] {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)
}

function tokenMatches(queryToken: string, candidateTokens: string[]): boolean {
  return candidateTokens.some((candidate) => candidate === queryToken || (queryToken.length >= 2 && candidate.startsWith(queryToken)))
}

/** Search item and category names with AND matching, then rank category+item matches first. */
export function searchMenuItems(items: MenuItem[], query: string): MenuItem[] {
  const queryTokens = [...new Set(normalizeMenuSearchText(query))]
  if (queryTokens.length === 0) return items

  return items
    .map((item, index) => {
      const itemTokens = normalizeMenuSearchText(item.name)
      const categoryTokens = normalizeMenuSearchText(item.category?.name)
      const searchableTokens = [...itemTokens, ...categoryTokens]
      if (!queryTokens.every((token) => tokenMatches(token, searchableTokens))) return null

      const matchesItem = queryTokens.filter((token) => tokenMatches(token, itemTokens)).length
      const matchesCategory = queryTokens.filter((token) => tokenMatches(token, categoryTokens)).length

      const itemHasAllTerms = matchesItem === queryTokens.length
      const categoryHasAllTerms = matchesCategory === queryTokens.length
      const matchedBothFields = matchesItem > 0 && matchesCategory > 0
      const rank = matchedBothFields ? 300 : itemHasAllTerms ? 200 : categoryHasAllTerms ? 100 : 150
      const exactName = itemTokens.join(' ') === queryTokens.join(' ')
      return { item, index, score: rank + (exactName ? 50 : 0) + matchesItem * 2 + matchesCategory }
    })
    .filter((entry): entry is { item: MenuItem; index: number; score: number } => entry !== null)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map(({ item }) => item)
}
